import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider } from './hooks/useAuth';
import { useAuth } from './hooks/useAuth';
import TopNav from './components/layout/TopNav';
import Footer from './components/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';
import NextStep from './components/NextStep';
import WorkflowRail from './components/layout/WorkflowRail';


import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// ─── Lazy-loaded feature pages ───────────────────────────────────────────────

const CasesPage = React.lazy(() => import('./pages/CasesPage'));
const CaseDetailPage = React.lazy(() => import('./pages/CaseDetailPage'));
const PanelPoolPage = React.lazy(() => import('./pages/PanelPoolPage'));
const TemplateLibraryPage = React.lazy(() => import('./pages/TemplateLibraryPage'));
const ArgumentRepositoryPage = React.lazy(() => import('./pages/ArgumentRepositoryPage'));
const HistoryPage = React.lazy(() => import('./pages/HistoryPage'));
const AIToolsPage = React.lazy(() => import('./pages/AIToolsPage'));
const MockJuryPage = React.lazy(() => import('./pages/MockJuryPage'));
const SimulationRunPage = React.lazy(() => import('./pages/SimulationRunPage'));
const SimulationResultsPage = React.lazy(() => import('./pages/SimulationResultsPage'));
const ComparePage = React.lazy(() => import('./pages/compare/ComparePage'));
const SettlementCalculatorPage = React.lazy(() => import('./pages/settlement/SettlementCalculatorPage'));

// AI Tools & Feature pages
const AnalyticsDashboardPage = React.lazy(() => import('./pages/analytics/AnalyticsDashboardPage'));
const AppellateTrackerPage = React.lazy(() => import('./pages/appeals/AppellateTrackerPage'));
const CalendarPage = React.lazy(() => import('./pages/calendar/CalendarPage'));
const TrialChecklistPage = React.lazy(() => import('./pages/checklist/TrialChecklistPage'));
const ClosingArgumentBuilderPage = React.lazy(() => import('./pages/closing/ClosingArgumentBuilderPage'));
const CaseCollaborationPage = React.lazy(() => import('./pages/collaboration/CaseCollaborationPage'));
const ClientCommunicationsPage = React.lazy(() => import('./pages/communications/ClientCommunicationsPage'));
const CrossExamGeneratorPage = React.lazy(() => import('./pages/cross_exam/CrossExamGeneratorPage'));
const DepositionPrepPage = React.lazy(() => import('./pages/depositions/DepositionPrepPage'));
const DiscoveryTrackerPage = React.lazy(() => import('./pages/discovery/DiscoveryTrackerPage'));
const DocumentTemplatesPage = React.lazy(() => import('./pages/documents/DocumentTemplatesPage'));
const EvidenceAnalyzerPage = React.lazy(() => import('./pages/evidence/EvidenceAnalyzerPage'));
const EvidenceUploadPage = React.lazy(() => import('./pages/evidence/EvidenceUploadPage'));
const ExhibitImpactPredictorPage = React.lazy(() => import('./pages/exhibits/ExhibitImpactPredictorPage'));
const ExpertWitnessPage = React.lazy(() => import('./pages/experts/ExpertWitnessPage'));
const PostTrialFeedbackPage = React.lazy(() => import('./pages/feedback/PostTrialFeedbackPage'));
const FirmDashboardPage = React.lazy(() => import('./pages/firm/FirmDashboardPage'));
const FirmMembersPage = React.lazy(() => import('./pages/firm/FirmMembersPage'));
const FirmSettingsPage = React.lazy(() => import('./pages/firm/FirmSettingsPage'));
const ArgumentGeneratorPage = React.lazy(() => import('./pages/generator/ArgumentGeneratorPage'));
const ClientIntakePage = React.lazy(() => import('./pages/intake/ClientIntakePage'));
const InvoiceGeneratorPage = React.lazy(() => import('./pages/invoices/InvoiceGeneratorPage'));
const PanelQuestionsPage = React.lazy(() => import('./pages/juror_questions/PanelQuestionsPage'));
const PanelInstructionsPage = React.lazy(() => import('./pages/jury_instructions/PanelInstructionsPage'));
const MediationPrepPage = React.lazy(() => import('./pages/mediation/MediationPrepPage'));
const CaseNotesPage = React.lazy(() => import('./pages/notes/CaseNotesPage'));
const OpeningStatementOptimizerPage = React.lazy(() => import('./pages/opening/OpeningStatementOptimizerPage'));
const PersonaPoolPage = React.lazy(() => import('./pages/personas/PersonaPoolPage'));
const PleaBargainAnalyzerPage = React.lazy(() => import('./pages/plea/PleaBargainAnalyzerPage'));
const PresentationScorerPage = React.lazy(() => import('./pages/presentation/PresentationScorerPage'));
const BriefResearchLibraryPage = React.lazy(() => import('./pages/research/BriefResearchLibraryPage'));
const CourtRulesPage = React.lazy(() => import('./pages/rules/CourtRulesPage'));
const ScenarioBuilderPage = React.lazy(() => import('./pages/scenarios/ScenarioBuilderPage'));
const SettlementDemandLetterPage = React.lazy(() => import('./pages/settlement_demand/SettlementDemandLetterPage'));
const CaseStrategyPage = React.lazy(() => import('./pages/strategy/CaseStrategyPage'));
const TaskManagementPage = React.lazy(() => import('./pages/tasks/TaskManagementPage'));
const TimeTrackingPage = React.lazy(() => import('./pages/time/TimeTrackingPage'));
const CaseTimelinePage = React.lazy(() => import('./pages/timeline/CaseTimelinePage'));
const ProfilePage = React.lazy(() => import('./pages/user/ProfilePage'));
const SettingsPage = React.lazy(() => import('./pages/user/SettingsPage'));
const NotificationSettingsPage = React.lazy(() => import('./pages/NotificationSettingsPage'));
const ArgumentVersionsPage = React.lazy(() => import('./pages/versions/ArgumentVersionsPage'));
const PanelSelectionPage = React.lazy(() => import('./pages/voir_dire/PanelSelectionPage'));

// Legal / marketing pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const HelpCenterPage = React.lazy(() => import('./pages/help/HelpCenterPage'));
const WalkthroughsIndexPage = React.lazy(() => import('./pages/help/WalkthroughsIndexPage'));
const WalkthroughPage = React.lazy(() => import('./pages/help/WalkthroughPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const PricingPage = React.lazy(() => import('./pages/legal/PricingPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/legal/TermsOfServicePage'));
const SupportPage = React.lazy(() => import('./pages/legal/SupportPage'));
const AboutPage = React.lazy(() => import('./pages/legal/AboutPage'));
const ExampleReportPage = React.lazy(() => import('./pages/legal/ExampleReportPage'));
const ArgumentWeaknessDetectorPage = React.lazy(() => import('./pages/weakness/ArgumentWeaknessDetectorPage'));
const WitnessesPage = React.lazy(() => import('./pages/witnesses/WitnessesPage'));

// ─── Loading fallback ────────────────────────────────────────────────────────

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading...</span>
      </div>
    </div>
  );
}

// ─── Routes ──────────────────────────────────────────────────────────────────

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  const redirectIfAuth = (element: React.ReactNode) =>
    isAuthenticated ? <Navigate to="/dashboard" replace /> : element;

  const protectedRoute = (element: React.ReactNode) => (
    <ProtectedRoute>{element}</ProtectedRoute>
  );

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={redirectIfAuth(<LoginPage />)} />
        <Route path="/register" element={redirectIfAuth(<RegisterPage />)} />
        <Route path="/forgot-password" element={redirectIfAuth(<ForgotPasswordPage />)} />
        <Route path="/reset-password" element={redirectIfAuth(<ResetPasswordPage />)} />

        {/* Core protected routes */}
        <Route path="/dashboard" element={protectedRoute(<DashboardPage />)} />
        <Route path="/cases" element={protectedRoute(<CasesPage />)} />
        <Route path="/cases/:caseId" element={protectedRoute(<CaseDetailPage />)} />
        <Route path="/ai-tools" element={protectedRoute(<AIToolsPage />)} />
        <Route path="/mock-panel" element={protectedRoute(<MockJuryPage />)} />
        <Route path="/panel-pool" element={protectedRoute(<PanelPoolPage />)} />
        <Route path="/templates" element={protectedRoute(<TemplateLibraryPage />)} />
        <Route path="/arguments" element={protectedRoute(<ArgumentRepositoryPage />)} />
        <Route path="/history" element={protectedRoute(<HistoryPage />)} />
        <Route path="/simulations/new" element={protectedRoute(<SimulationRunPage />)} />
        <Route path="/simulations/:id" element={protectedRoute(<SimulationRunPage />)} />
        <Route path="/simulations/:id/results" element={protectedRoute(<SimulationResultsPage />)} />
        <Route path="/compare" element={protectedRoute(<ComparePage />)} />
        <Route path="/settlement" element={protectedRoute(<SettlementCalculatorPage />)} />

        {/* AI Tools & Feature pages */}
        <Route path="/analytics" element={protectedRoute(<AnalyticsDashboardPage />)} />
        <Route path="/appeals" element={protectedRoute(<AppellateTrackerPage />)} />
        <Route path="/calendar" element={protectedRoute(<CalendarPage />)} />
        <Route path="/checklist" element={protectedRoute(<TrialChecklistPage />)} />
        <Route path="/closing" element={protectedRoute(<ClosingArgumentBuilderPage />)} />
        <Route path="/collaboration" element={protectedRoute(<CaseCollaborationPage />)} />
        <Route path="/communications" element={protectedRoute(<ClientCommunicationsPage />)} />
        <Route path="/cross-exam" element={protectedRoute(<CrossExamGeneratorPage />)} />
        <Route path="/depositions" element={protectedRoute(<DepositionPrepPage />)} />
        <Route path="/discovery" element={protectedRoute(<DiscoveryTrackerPage />)} />
        <Route path="/documents" element={protectedRoute(<DocumentTemplatesPage />)} />
        <Route path="/evidence" element={<Navigate to="/evidence/analyzer" replace />} />
        <Route path="/evidence/analyzer" element={protectedRoute(<EvidenceAnalyzerPage />)} />
        <Route path="/evidence/upload" element={protectedRoute(<EvidenceUploadPage />)} />
        <Route path="/exhibits" element={protectedRoute(<ExhibitImpactPredictorPage />)} />
        <Route path="/experts" element={protectedRoute(<ExpertWitnessPage />)} />
        <Route path="/feedback" element={protectedRoute(<PostTrialFeedbackPage />)} />
        <Route path="/firm" element={protectedRoute(<FirmDashboardPage />)} />
        <Route path="/firm/members" element={protectedRoute(<FirmMembersPage />)} />
        <Route path="/firm/settings" element={protectedRoute(<FirmSettingsPage />)} />
        <Route path="/generator" element={protectedRoute(<ArgumentGeneratorPage />)} />
        <Route path="/intake" element={protectedRoute(<ClientIntakePage />)} />
        <Route path="/invoices" element={protectedRoute(<InvoiceGeneratorPage />)} />
        <Route path="/panel-questions" element={protectedRoute(<PanelQuestionsPage />)} />
        <Route path="/panel-instructions" element={protectedRoute(<PanelInstructionsPage />)} />
        <Route path="/mediation" element={protectedRoute(<MediationPrepPage />)} />
        <Route path="/notes" element={protectedRoute(<CaseNotesPage />)} />
        <Route path="/opening" element={protectedRoute(<OpeningStatementOptimizerPage />)} />
        <Route path="/personas" element={protectedRoute(<PersonaPoolPage />)} />
        <Route path="/plea" element={protectedRoute(<PleaBargainAnalyzerPage />)} />
        <Route path="/presentation" element={protectedRoute(<PresentationScorerPage />)} />
        <Route path="/research" element={protectedRoute(<BriefResearchLibraryPage />)} />
        <Route path="/rules" element={protectedRoute(<CourtRulesPage />)} />
        <Route path="/scenarios" element={protectedRoute(<ScenarioBuilderPage />)} />
        <Route path="/settlement-demand" element={protectedRoute(<SettlementDemandLetterPage />)} />
        <Route path="/strategy" element={protectedRoute(<CaseStrategyPage />)} />
        <Route path="/tasks" element={protectedRoute(<TaskManagementPage />)} />
        <Route path="/time" element={protectedRoute(<TimeTrackingPage />)} />
        <Route path="/timeline" element={protectedRoute(<CaseTimelinePage />)} />
        <Route path="/profile" element={protectedRoute(<ProfilePage />)} />
        <Route path="/settings" element={protectedRoute(<SettingsPage />)} />
        <Route path="/settings/notifications" element={protectedRoute(<NotificationSettingsPage />)} />
        <Route path="/versions" element={protectedRoute(<ArgumentVersionsPage />)} />
        <Route path="/panel-selection" element={protectedRoute(<PanelSelectionPage />)} />
        <Route path="/weakness" element={protectedRoute(<ArgumentWeaknessDetectorPage />)} />
        <Route path="/witnesses" element={protectedRoute(<WitnessesPage />)} />

        {/* Public help center */}
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/help/walkthroughs" element={<WalkthroughsIndexPage />} />
        <Route path="/help/walkthroughs/:slug" element={<WalkthroughPage />} />
        <Route path="/help/:sectionId" element={<HelpCenterPage />} />

        {/* Public legal pages */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/example-report" element={<ExampleReportPage />} />

        {/* Public homepage — viewable signed in or out (HomePage swaps the
            sign-up CTAs for a Dashboard button when authenticated). Previously
            this hard-redirected authed users to /dashboard, which made the
            marketing site unreachable with a saved login. */}
        <Route path="/" element={<HomePage />} />

        {/* Legacy URL redirects */}
        <Route path="/create-jury" element={<Navigate to="/mock-panel" replace />} />
        <Route path="/mock-jury" element={<Navigate to="/mock-panel" replace />} />
        <Route path="/jury-pool" element={<Navigate to="/panel-pool" replace />} />
        <Route path="/juror-questions" element={<Navigate to="/panel-questions" replace />} />
        <Route path="/jury-instructions" element={<Navigate to="/panel-instructions" replace />} />
        <Route path="/voir-dire" element={<Navigate to="/panel-selection" replace />} />

        {/* Unmatched routes previously rendered a blank page */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

// ─── Layout ──────────────────────────────────────────────────────────────────

function AppLayout() {
  const { isAuthenticated, isDemoMode } = useAuth();
  const location = useLocation();
  // Marketing pages carry their own header/footer even for signed-in users —
  // rendering the app TopNav + pt-16 + Footer around them produced a double
  // header on `/` once the homepage became viewable while authenticated.
  const isBareMarketingRoute = ['/', '/about', '/example-report'].includes(location.pathname);
  const showAppChrome = isAuthenticated && !isBareMarketingRoute;

  return (
    <div className="flex flex-col min-h-screen bg-paper-200">
      {showAppChrome && <TopNav />}
      {showAppChrome && isDemoMode && (
        <div className="fixed top-16 left-0 right-0 z-30 bg-amber-100 border-b border-amber-300 text-amber-900 text-sm text-center py-1.5 px-4">
          Demo Mode: you're viewing sample data. Anything you create here (cases, arguments, simulations, etc.) is <strong>not saved to your account</strong>.
        </div>
      )}
      {/* `pt-16 pt-8` used to be emitted together in demo mode, and Tailwind
          resolves that pair to 32px, not 64+32 — so the demo banner sat on top
          of every page heading. One class per case instead.

          `overflow-y-auto` was also removed: main grows with its content
          inside a min-h-screen column, so it never actually scrolled (the
          document does, which is why SimulationRunPage calls window.scrollTo).
          Its only live effect was making main a scrollport, which silently
          disabled position:sticky for anything inside it. */}
      <main
        className={`flex-1 ${showAppChrome ? (isDemoMode ? 'pt-24' : 'pt-16') : ''}`}
      >
        {showAppChrome ? (
          // Page content and the workflow rail share a row at `xl`; below that
          // the rail stacks underneath so narrow viewports keep the full width
          // for the page itself. Items stretch on purpose: the rail column has
          // to be as tall as the page for its sticky inner panel to have any
          // travel to stick over.
          <div className="flex flex-col xl:flex-row min-h-full">
            <div className="flex-1 min-w-0">
              <AppRoutes />
            </div>
            <WorkflowRail stickyTop={isDemoMode ? 96 : 64} />
          </div>
        ) : (
          <AppRoutes />
        )}
      </main>
      {showAppChrome && <Footer />}
    </div>
  );
}

// ─── App ─────────────────────────────────────────────────────────────────────

function App() {
  return (
    <AuthProvider>
      <AppLayout />
    </AuthProvider>
  );
}

export default App;
